import { CurrentUserOrganizationDto, OrganizationDto } from '@sib/api';
import { create, StateCreator } from 'zustand';
import { createJSONStorage, persist, StateStorage } from 'zustand/middleware';
import {
  customExtensionStorage,
  shouldUseExtensionStorage,
} from './extension-storage';

const USER_SETUP_STORE_STORAGE_KEY = 'user-setup.store';

interface UserSetupOrganization {
  id: string;
  name: string;
}

interface UserSetupStore {
  previousOrganization: UserSetupOrganization | null;
  organization: UserSetupOrganization;
  setOrganization: (organization: Partial<UserSetupOrganization>) => void;
  hasGlobalAccess: boolean | null;
  setHasGlobalAccess: (hasGlobalAccess: boolean) => void;
  isSetupCompleted?: boolean;
  setIsSetupCompleted: (isSetupCompleted: boolean) => void;
  organizations: CurrentUserOrganizationDto[];
  setOrganizations: (organizations: CurrentUserOrganizationDto[]) => void;
  parentOrganization: OrganizationDto | null;
  setParentOrganization: (org: OrganizationDto | null) => void;
  entryPointOrganization: OrganizationDto | null;
  setEntryPointOrganization: (org: OrganizationDto | null) => void;
  actionPermissions: Record<string, number> | null;
  setActionPermissions: (permissions: Record<string, number>) => void;
  globalActionPermissions: Record<string, number> | null;
  setGlobalActionPermissions: (permissions: Record<string, number>) => void;
}

const stateCreator: StateCreator<UserSetupStore> = (set, getState) => ({
  organization: {
    id: '',
    name: '',
  },
  previousOrganization: {
    id: '',
    name: '',
  },
  organizationId: '',
  organizations: [],
  hasGlobalAccess: null,
  setHasGlobalAccess: (hasGlobalAccess: boolean) => {
    set({ hasGlobalAccess });
  },
  isSetupCompleted: false,
  setIsSetupCompleted: (isSetupCompleted: boolean) => {
    set({ isSetupCompleted });
  },
  setOrganization: organization => {
    const currentOrganization = getState().organization;

    if (
      currentOrganization.id === organization.id &&
      currentOrganization.name === organization.name
    )
      return;

    set({
      previousOrganization: currentOrganization,
      organization: {
        ...currentOrganization,
        ...organization,
      },
    });
  },
  setOrganizations: (organizations: CurrentUserOrganizationDto[]) =>
    set({ organizations }),
  parentOrganization: null,
  setParentOrganization: (org: OrganizationDto | null) =>
    set({ parentOrganization: org }),
  entryPointOrganization: null,
  setEntryPointOrganization: (org: OrganizationDto | null) =>
    set({ entryPointOrganization: org }),
  actionPermissions: {},
  setActionPermissions: (permissions: Record<string, number>) =>
    set({ actionPermissions: permissions }),
  globalActionPermissions: null,
  setGlobalActionPermissions: (permissions: Record<string, number>) =>
    set({ globalActionPermissions: permissions }),
});

export const useUserSetupStore = create<UserSetupStore>()(
  persist(stateCreator, {
    name: USER_SETUP_STORE_STORAGE_KEY,
    storage: createJSONStorage(() =>
      shouldUseExtensionStorage
        ? (customExtensionStorage as StateStorage)
        : localStorage
    ),
    partialize: ({
      organization,
      entryPointOrganization,
      parentOrganization,
      actionPermissions,
      globalActionPermissions,
    }) => ({
      organization,
      entryPointOrganization,
      parentOrganization,
      globalActionPermissions,
      actionPermissions,
    }),
  })
);
